import { normalizeChain, normalizeCanvasAddr, isPlaceholderChain } from './canvasSync';
import { detectChainStrict } from './chainDetect';
import { isPlaceholderId, isPlaceholderNode } from './placeholderNode';

export interface HolisticCanvasCandidate {
    address: string;
    /** Human-readable hint shown in the picker (entity name or canvas label). */
    label: string;
    entityName?: string | null;
    chain?: string | null;
}

const HIDDEN_VISIBILITY = 0;

function truncateAddr(addr: string): string {
    if (!addr || addr.length < 12) return addr || '';
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
}

/** Case-aware membership check for holistic trace address lists. */
export function holisticAddressInList(address: string, list: string[]): boolean {
    const norm = normalizeCanvasAddr(address);
    return list.some((item) => normalizeCanvasAddr(item) === norm);
}

function resolveNodeChain(nodeData: Record<string, any>): string | null {
    const fromField = !isPlaceholderChain(nodeData.chain)
        ? normalizeChain(String(nodeData.chain))
        : null;
    if (fromField && !isPlaceholderChain(fromField)) return fromField;
    const detected = detectChainStrict(nodeData.id);
    return detected ? normalizeChain(detected) : null;
}

function isTraceableCanvasNode(nodeData: Record<string, any> | undefined): nodeData is Record<string, any> {
    if (!nodeData?.id || nodeData.source) return false;
    if (isPlaceholderId(nodeData.id) || isPlaceholderNode(nodeData)) return false;
    const nodeType = nodeData.type;
    if (nodeType && nodeType !== 'address') return false;
    if (nodeData._visibility === HIDDEN_VISIBILITY) return false;
    return true;
}

/**
 * Addresses visible on the Workbench canvas that can be added to a holistic trace.
 * Excludes placeholders, hidden nodes, wrong-chain wallets, and already-selected seeds.
 */
export function listHolisticCanvasCandidates(
    elements: Array<{ data?: Record<string, any> }>,
    traceChain: string,
    selectedAddresses: string[],
): HolisticCanvasCandidate[] {
    const chain = normalizeChain(traceChain);
    const seen = new Set<string>();
    const out: HolisticCanvasCandidate[] = [];

    for (const el of elements) {
        const data = el.data;
        if (!isTraceableCanvasNode(data)) continue;

        const address = String(data.id);
        if (holisticAddressInList(address, selectedAddresses)) continue;

        const nodeChain = resolveNodeChain(data);
        if (nodeChain && nodeChain !== chain) continue;

        const key = normalizeCanvasAddr(address);
        if (seen.has(key)) continue;
        seen.add(key);

        const entityName = data.entity_name || data.label || null;
        const label = entityName && entityName !== address
            ? String(entityName)
            : truncateAddr(address);

        out.push({
            address,
            label,
            entityName: data.entity_name ?? null,
            chain: nodeChain,
        });
    }

    out.sort((a, b) => {
        const aNamed = a.entityName ? 0 : 1;
        const bNamed = b.entityName ? 0 : 1;
        if (aNamed !== bNamed) return aNamed - bNamed;
        return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    });

    return out;
}

/** Filter canvas candidates while the user types in the add-address field. */
export function filterHolisticCanvasCandidates(
    candidates: HolisticCanvasCandidate[],
    query: string,
): HolisticCanvasCandidate[] {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) =>
        c.address.toLowerCase().includes(q)
        || c.label.toLowerCase().includes(q)
        || (c.entityName?.toLowerCase().includes(q) ?? false),
    );
}
